import {
	createAnalytics,
	classifyIdbError,
	isRecoverableIdbError,
	type Analytics as AnalyticsClient,
	type AnalyticsError,
} from '@mkt-client/analytics';
import { createEventLogApiTransport } from '@mkt-client/analytics/transports/event-log-api';
import { get } from 'svelte/store';
import { ApiService } from '$lib/services/api';
import { ProductExperienceService } from '$lib/services/product-experience';
import { Sentry } from '$lib/services/sentry';
import { app } from '$lib/stores/app/store';
import { getDeviceId } from '$lib/utils/device-id.js';
import { resolveClientPlatform } from '$lib/stores/app/client-platform';
import type { App as AppNamespace } from '$lib/stores/app/types';
import { EVENT_TYPES, type KnownEvents as GeneratedKnownEvents } from '$events';
import { MANUAL_EVENT_TYPES, type ManualKnownEvents } from './generated/global.manual';

type AppStore = AppNamespace.AppStore;

type KnownEvents = GeneratedKnownEvents & ManualKnownEvents;

/** Minimal shape of SvelteKit's `beforeNavigate` callback argument. */
type BeforeNavigatePayload = {
	from: { url: URL } | null;
};

const PIPELINE_NAME = 'marketing';
const SOURCE_NAME = 'web-client-classic';
const SPEC_VERSION = '1.0.0';

/** Placeholder until the funnel-name taxonomy lands. */
const FUNNEL_NAME_PLACEHOLDER = 'tbd';

const DEFAULT_SAFE_ACTION = 'analytics: sub-collector failed';

/** In-memory SPA previous page URL (origin + pathname only). */
let previousPageUrl = '';

/** Suppresses repeated Sentry noise after bootstrap IDB failure. */
let idbDegradedReported = false;

type IdbReportingTier = 'recovered' | 'degraded' | 'exception';

/**
 * Sentry `extra.action` tag for envelope failures inside {@link Analytics.log}.
 * @param {string} type Wire-format event type literal.
 * @returns {string}
 */
const defaultLogSafeAction = (type: string): string =>
	`Analytics.log[${type}]: envelope build failed`;

/**
 * Caller-supplied `data` slot: schema fields minus `aofl_product`
 * (injected by `Analytics.log`), plus an open index signature for
 * forward-compatible extras (e.g. `user_to_product_uuid`).
 */
export type EventData<T extends keyof KnownEvents> = {
	[K in keyof KnownEvents[T]['data'] as string extends K
		? never
		: K extends 'aofl_product'
			? never
			: K]: KnownEvents[T]['data'][K];
} & Record<string, unknown>;

/**
 * Browser-owned "v1 client slice" sent in the multipart `client_context`
 * field. The backend builds `pipeline_context.client` from this plus its
 * own server-stamped fields when `schema_version === 'v1'`.
 */
export interface BasePipelineContext {
	client: {
		schema_version: 'v1';
		is_automated_test: boolean;
		preserve_session_device_id: boolean;
		client_platform?: string;
		page_info: {
			funnel_name: string;
			page_name: string;
			page_url: string;
			previous_page_url: string;
			referrer: string;
			url_params: Record<string, unknown>;
		};
	};
	server: Record<string, never>;
}

/**
 * Shared envelope; callers spread and add their `data` payload.
 * Several top-level fields (`uuid`, `specversion`, `source`,
 * `source_version`, `pipeline`, `event_type`, `type`) are stamped
 * server-side on ingest but required by `base_event.json`; drop
 * once upstream marks them optional.
 */
export interface BaseEventEnvelope<TType extends string> {
	uuid: string;
	timestamp: string;
	specversion: string;
	source: string;
	source_version: string;
	pipeline: string;
	event_type: TType;
	type: TType;
	device_id: string;
	pipeline_context: BasePipelineContext;
}

/**
 * Wire-type for `createAnalytics`. The index signature is required
 * to satisfy the `Record<string, AnalyticsEvent>` constraint.
 *
 * TODO(type-first-schema): Tighten compile-time checks against generated schemas.
 * 1. Remove `[key: string]: unknown` from SchemaEventMap.
 * 2. Replace `as unknown as SchemaEventMap[T]` with `satisfies ClientEmitEvent<T>`.
 * 3. Emit `ClientEmitEvent` from event-generator (client-owned fields only).
 * 4. Derive envelope/pipeline types from `$events` instead of hand-written interfaces.
 * See: packages/client-analytics/scripts/event-generator.ts (x-stamped-by: server).
 */
type SchemaEventMap = {
	[K in keyof KnownEvents]: KnownEvents[K] & {
		type: K;
		[key: string]: unknown;
	};
};

/** Fallback when `buildPipelineContext` throws. */
const EMPTY_PIPELINE_CONTEXT: BasePipelineContext = {
	client: {
		schema_version: 'v1',
		is_automated_test: false,
		preserve_session_device_id: false,
		page_info: {
			funnel_name: FUNNEL_NAME_PLACEHOLDER,
			page_name: '',
			page_url: '',
			previous_page_url: '',
			referrer: '',
			url_params: {},
		},
	},
	server: {},
};

/**
 * Single dispatch entry-point for every schema-shaped analytics
 * event. Owns the shared envelope, pipeline-context, and Sentry-
 * tagged safe runners so collectors only assemble their `data`
 * slot.
 */
export class Analytics {
	static readonly PIPELINE_NAME = PIPELINE_NAME;
	static readonly SOURCE_NAME = SOURCE_NAME;
	static readonly SPEC_VERSION = SPEC_VERSION;
	static readonly FUNNEL_NAME_PLACEHOLDER = FUNNEL_NAME_PLACEHOLDER;
	static readonly EMPTY_PIPELINE_CONTEXT = EMPTY_PIPELINE_CONTEXT;

	private static clientPromise: Promise<AnalyticsClient<SchemaEventMap>> | null = null;

	/**
	 * Drains the outbound analytics queue and awaits in-flight delivery.
	 * Use before navigation when the page must not tear down until events POST.
	 * @returns {Promise<void>}
	 */
	static async flush(): Promise<void> {
		const analytics = await Analytics.getClient();
		await analytics.flush();
	}

	/**
	 * Emits a schema-shaped event. Callers supply only the
	 * event-specific `data` — envelope and `aofl_product` are stamped.
	 * @param {keyof KnownEvents} type Wire-format `event_type` literal from `EVENT_TYPES`.
	 * @param {EventData} data Event-specific fields.
	 * @param {object} [options] Optional envelope failure tagging.
	 * @param {string} [options.safeAction] Sentry `extra.action` tag for envelope failures; defaults to one derived from `type`.
	 * @returns {Promise<void>}
	 */
	static async log<T extends keyof KnownEvents>(
		type: T,
		data: EventData<T>,
		options: { safeAction?: string } = {}
	): Promise<void> {
		const analytics = await Analytics.getClient();
		const safeAction = options.safeAction ?? defaultLogSafeAction(String(type));
		const { safe } = Analytics.createSafeRunner(safeAction);
		const envelope = Analytics.buildBaseEnvelope(type, { safeAction });
		const aofl_product = safe(() => ProductExperienceService.AOFL_PRODUCT_NAME, '');
		const payload = {
			...envelope,
			data: { aofl_product, ...data },
		} as unknown as SchemaEventMap[T];
		await analytics.log(payload);
	}

	/**
	 * Returns the shared envelope; callers spread it and add `data`.
	 * @param {string} eventType Wire-format `event_type` literal.
	 * @param {object} [options] Optional app-state override and failure tagging.
	 * @param {AppStore} [options.appState] Defaults to `get(app)`.
	 * @param {string} [options.safeAction] Sentry tag for sub-collector failures.
	 * @returns {BaseEventEnvelope}
	 */
	static buildBaseEnvelope<TType extends string>(
		eventType: TType,
		options: { appState?: AppStore; safeAction?: string } = {}
	): BaseEventEnvelope<TType> {
		const { appState = get(app), safeAction = DEFAULT_SAFE_ACTION } = options;
		const { safe } = Analytics.createSafeRunner(safeAction);
		return {
			uuid: crypto.randomUUID(),
			timestamp: String(Date.now()),
			specversion: SPEC_VERSION,
			source: SOURCE_NAME,
			source_version: __APP_VERSION__,
			pipeline: PIPELINE_NAME,
			event_type: eventType,
			type: eventType,
			device_id: safe(() => getDeviceId() ?? '', ''),
			pipeline_context: safe(
				() => Analytics.buildPipelineContext(appState),
				EMPTY_PIPELINE_CONTEXT
			),
		};
	}

	/**
	 * Builds the browser-owned v1 client slice. The backend reads this
	 * from the multipart `client_context` field and stamps its own
	 * server-side fields (IP, session, viewer flags) on ingest.
	 * @param {AppStore} appState Snapshot of the app store.
	 * @returns {BasePipelineContext}
	 */
	static buildPipelineContext(appState: AppStore): BasePipelineContext {
		const clientPlatform =
			resolveClientPlatform(appState.meta, appState.global?.linkFromAppPlatform) ?? '';
		const { safe } = Analytics.createSafeRunner(DEFAULT_SAFE_ACTION);

		return {
			client: {
				schema_version: 'v1',
				is_automated_test: appState.meta.isAutomatedTesting ?? false,
				preserve_session_device_id: true,
				client_platform: clientPlatform,
				page_info: {
					funnel_name: FUNNEL_NAME_PLACEHOLDER,
					page_name: safe(() => Analytics.createPageName(), ''),
					page_url: safe(() => `${location.origin}${location.pathname}`, ''),
					previous_page_url: safe(() => Analytics.getPreviousPageUrl(), ''),
					referrer: safe(() => document.referrer || '', ''),
					url_params: safe(() => Analytics.getUrlVariableInfo(), {}),
				},
			},
			server: {},
		};
	}

	/**
	 * Derives a semantic page name from the current pathname.
	 * @returns {string}
	 */
	static createPageName(): string {
		if (location.pathname === '/') return 'home';
		return location.pathname.replace(/^\//, '').replace(/\/$/, '').replace(/\//g, '-');
	}

	/**
	 * Collects current URL query parameters as a plain object.
	 * @returns {Record<string, unknown>}
	 */
	static getUrlVariableInfo(): Record<string, unknown> {
		const searchParams = new URLSearchParams(location.search);
		const params: Record<string, unknown> = {};
		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}
		return params;
	}

	/**
	 * Records the page the user is leaving before an in-app navigation.
	 * @param {BeforeNavigatePayload} navigation SvelteKit beforeNavigate payload.
	 */
	static updatePreviousPageBeforeNavigate(navigation: BeforeNavigatePayload): void {
		if (!navigation.from?.url) return;
		previousPageUrl = Analytics.sanitizePageUrl(navigation.from.url);
	}

	/**
	 * Returns the last in-app page URL, or the browser entry referrer when unset.
	 * @returns {string}
	 */
	static getPreviousPageUrl(): string {
		if (previousPageUrl.length > 0) return previousPageUrl;
		return document.referrer || '';
	}

	/**
	 * @param {URL} url Page URL to sanitize.
	 * @returns {string} Origin and pathname only (no query or hash).
	 */
	private static sanitizePageUrl(url: URL): string {
		return `${url.origin}${url.pathname}`;
	}

	/**
	 * Returns `safe`/`safeAsync` wrappers that report to Sentry with
	 * the given `extra.action` tag on throw.
	 * @param {string} action Sentry `extra.action` tag.
	 * @returns {{ safe, safeAsync }}
	 */
	static createSafeRunner(action: string): {
		safe: <T>(fn: () => T, fallback: T) => T;
		safeAsync: <T>(fn: () => Promise<T>, fallback: T) => Promise<T>;
	} {
		return {
			safe<T>(fn: () => T, fallback: T): T {
				try {
					return fn();
				} catch (error) {
					Sentry.captureException(error, { extra: { action } });
					return fallback;
				}
			},
			async safeAsync<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
				try {
					return await fn();
				} catch (error) {
					Sentry.captureException(error, { extra: { action } });
					return fallback;
				}
			},
		};
	}

	/**
	 * Returns the lazily-bootstrapped client. Exposed so app-init can
	 * pre-warm and tests can introspect; call sites should use `log`.
	 * @returns {Promise<AnalyticsClient<SchemaEventMap>>}
	 */
	static getClient(): Promise<AnalyticsClient<SchemaEventMap>> {
		if (Analytics.clientPromise === null) {
			Analytics.clientPromise = Analytics.bootstrap();
		}
		return Analytics.clientPromise;
	}

	/** Drops the cached client so tests can re-bootstrap with fresh mocks. */
	static resetForTesting(): void {
		Analytics.clientPromise = null;
		previousPageUrl = '';
		idbDegradedReported = false;
	}

	private static classifyErrorReportingTier(err: AnalyticsError): IdbReportingTier {
		if (!isRecoverableIdbError(err.cause)) {
			return 'exception';
		}
		// If the store failed to open, we're degraded and will dispatch events directly to transports.
		if (err.source === 'event-store-open') {
			return 'degraded';
		}
		// If the store operation failed, we're recovered (expected failure) and will retry.
		if (err.source === 'event-store-operation' || err.source === 'event-store-append') {
			return 'recovered';
		}
		return 'exception';
	}

	/**
	 * Routes every pipeline error to Sentry. Consumed as `onError` by
	 * `createAnalytics` so the analytics stream never throws unhandled
	 * rejections regardless of IDB availability or transport failures.
	 * @param {AnalyticsError} err Error emitted by the analytics package.
	 */
	private static handleAnalyticsError(err: AnalyticsError): void {
		const extra = {
			action: `analytics: ${err.source}`,
			event_type: err.event?.type,
			transport: err.transport,
		};

		const tier = Analytics.classifyErrorReportingTier(err);

		if (tier === 'recovered') {
			Sentry.addBreadcrumb({
				category: 'analytics',
				message: `IndexedDB ${classifyIdbError(err.cause) ?? 'error'} during ${err.source}`,
				level: 'warning',
				data: extra,
			});
			return;
		}

		if (tier === 'degraded') {
			if (idbDegradedReported) return;
			idbDegradedReported = true;
			Sentry.captureMessage(
				'Analytics IndexedDB unavailable — session running in direct-dispatch mode',
				{
					level: 'warning',
					extra: {
						...extra,
						idb_error_kind: classifyIdbError(err.cause),
					},
				}
			);
			return;
		}

		Sentry.captureException(err.cause instanceof Error ? err.cause : new Error(String(err.cause)), {
			extra,
		});
	}

	/**
	 * One-time client bootstrap. Migrating a new event = add one
	 * `addTransport` line here + one `Analytics.log` call site.
	 * @returns {Promise<AnalyticsClient<SchemaEventMap>>}
	 */
	private static async bootstrap(): Promise<AnalyticsClient<SchemaEventMap>> {
		const analytics = await createAnalytics<SchemaEventMap>({
			onError: Analytics.handleAnalyticsError,
		});
		const eventLogTransport = createEventLogApiTransport({ api: ApiService });
		analytics.addTransport(EVENT_TYPES.CLIENT_SESSION_INIT, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.ACCOUNT_SETTINGS_UPDATE, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_ACCESS_DENIED, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_CAMPAIGN_CLICK, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_DATA_ANOMALY, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_MARKETING_EXIT, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_PAGE_LINK_CLICK, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_PAGE_LOAD, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_PAYMENT_FLOW_STATE, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_PRODUCT_EXPERIENCE_SWITCH, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.CLIENT_QR_CODE_VISIT, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.FORM_SUBMISSION, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.UI_SHOW, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.VIDEO_START, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.VIDEO_PAUSE, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.VIDEO_UNPAUSE, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.VIDEO_SEEK, eventLogTransport);
		analytics.addTransport(EVENT_TYPES.VIDEO_COMPLETE, eventLogTransport);
		// Manually added because event-generate-types is not generating these events.
		analytics.addTransport(MANUAL_EVENT_TYPES.SYSTEM_OAUTH_VIEW_START, eventLogTransport);
		analytics.addTransport(MANUAL_EVENT_TYPES.SYSTEM_OAUTH_VIEW_END, eventLogTransport);
		return analytics;
	}
}
